import { useState, useEffect } from 'react';
import { CheckCircleIcon, CheckIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import RegistrationModal from '../../components/RegistrationModal';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface RegistrationFee {
  amount: number;
  currency: string;
  description: string;
}

interface RegistrationStep {
  title: string;
  description: string;
  date: string;
}

function calcTimeLeft(target: Date): TimeLeft {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / 1000 / 60) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default function MobileRegistration() {
  const [isOpen, setIsOpen] = useState(false);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(false);
  const [isDatePassed, setIsDatePassed] = useState(false);
  const [registrationCount, setRegistrationCount] = useState(0);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [reservedSpots, setReservedSpots] = useState(0);
  const [isCapacityReached, setIsCapacityReached] = useState(false);
  const [openingDate, setOpeningDate] = useState<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(true);

  const [registrationFee, setRegistrationFee] = useState<RegistrationFee>({
    amount: 50000,
    currency: '₦',
    description: 'Per player, inclusive of all tournament amenities',
  });
  const [feeInclusions, setFeeInclusions] = useState<string[]>([]);
  const [eligibilityCriteria, setEligibilityCriteria] = useState<string[]>([]);
  const [registrationSteps, setRegistrationSteps] = useState<RegistrationStep[]>([]);
  const [rules, setRules] = useState<string[]>([]);
  const [importantNotice, setImportantNotice] = useState('');

  const isRegistrationOpen = isRegistrationEnabled && isDatePassed;
  const showCountdown = isRegistrationEnabled && !isDatePassed && openingDate !== null;

  useEffect(() => {
    fetchData();
    fetchContent();
  }, []);

  useEffect(() => {
    if (!openingDate || isDatePassed) return;
    setTimeLeft(calcTimeLeft(openingDate));
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(openingDate)), 1000);
    return () => clearInterval(timer);
  }, [openingDate, isDatePassed]);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('registration_content')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      if (data) {
        data.forEach((item) => {
          switch (item.content_key) {
            case 'registration_fee':
              setRegistrationFee(item.content_value);
              break;
            case 'fee_inclusions':
              setFeeInclusions(item.content_value);
              break;
            case 'eligibility_criteria':
              setEligibilityCriteria(item.content_value);
              break;
            case 'registration_steps':
              setRegistrationSteps(item.content_value);
              break;
            case 'tournament_rules':
              setRules(item.content_value);
              break;
            case 'important_notice':
              setImportantNotice(item.content_value);
              break;
          }
        });
      }
    } catch (error) {
      console.error('Error fetching registration content:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [tourneyRes, regEnabledRes, reservedRes] = await Promise.all([
        supabase
          .from('tournaments')
          .select('id, capacity, registration_open_date')
          .eq('status', 'upcoming')
          .order('start_date', { ascending: true })
          .limit(1),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'registration_enabled')
          .maybeSingle(),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'reserved_spots')
          .maybeSingle(),
      ]);

      const tournament = tourneyRes.data?.[0] ?? null;
      const capacity = tournament?.capacity ?? 250;
      if (tournament) setTotalCapacity(capacity);

      const reserved = reservedRes.data?.value
        ? (typeof reservedRes.data.value === 'number'
          ? reservedRes.data.value
          : parseInt(String(reservedRes.data.value)))
        : 0;
      setReservedSpots(isNaN(reserved) ? 0 : reserved);

      const playerQuery = supabase
        .from('players')
        .select('*', { count: 'exact', head: true });
      if (tournament?.id) playerQuery.eq('tournament_id', tournament.id);
      const { count } = await playerQuery;
      const playerCount = count ?? 0;
      if (count !== null) setRegistrationCount(playerCount);

      const effectiveCapacity = capacity - (isNaN(reserved) ? 0 : reserved);
      setIsCapacityReached(playerCount >= effectiveCapacity);

      const regEnabled = regEnabledRes.data
        ? regEnabledRes.data.value === true || regEnabledRes.data.value === 'true'
        : false;
      setIsRegistrationEnabled(regEnabled);

      let resolvedOpeningDate: Date | null = null;

      if (tournament?.registration_open_date) {
        resolvedOpeningDate = new Date(tournament.registration_open_date);
      }

      if (resolvedOpeningDate) {
        setOpeningDate(resolvedOpeningDate);
        setIsDatePassed(new Date() >= resolvedOpeningDate);
      } else {
        setIsDatePassed(true);
      }
    } catch (error) {
      console.error('Error fetching registration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const effectiveCapacity = totalCapacity - reservedSpots;
  const pct = Math.min(100, Math.round(((registrationCount + reservedSpots) / totalCapacity) * 100));
  const spotsLeft = effectiveCapacity - registrationCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-2 pb-10">
      {/* Status Card */}
      {isCapacityReached ? (
        <div className="bg-black/80 backdrop-blur-md rounded-xl p-4 border border-white/10 mb-5 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <h3 className="text-lg font-bold text-white mb-1">Registration Closed</h3>
          <p className="text-golf-sand text-sm leading-snug">
            The tournament has reached its maximum capacity of {totalCapacity} players.
            Please check back next year.
          </p>
        </div>
      ) : (
        <div className={`rounded-2xl p-5 mb-5 ${isRegistrationOpen ? 'bg-golf-green' : 'bg-gray-800'} text-white`}>
          <div className="flex items-center gap-2 mb-3">
            {isRegistrationOpen ? (
              <CheckCircleIcon className="w-5 h-5 text-green-300" />
            ) : (
              <ClockIcon className="w-5 h-5 text-yellow-300" />
            )}
            <span className={`text-sm font-semibold ${isRegistrationOpen ? 'text-green-300' : 'text-yellow-300'}`}>
              {isRegistrationOpen ? 'Registration Open' : showCountdown ? 'Registration Opening Soon' : 'Registration Closed'}
            </span>
          </div>

          {showCountdown && openingDate && (
            <p className="text-golf-sand text-sm mb-3">
              Opens on {openingDate.toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}

          {isRegistrationOpen && (
            <>
              <div className="flex justify-between mb-2">
                <span className="text-golf-sand text-sm">{registrationCount + reservedSpots} registered</span>
                <span className="text-white font-bold">{spotsLeft} left</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2 mb-3">
                <div
                  className={`h-2 rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : 'bg-golf-orange'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {pct >= 75 ? (
                <div className="flex items-start gap-2 text-amber-400 text-sm mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-shrink-0 mt-0.5">
                    <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium">Hurry! Only {spotsLeft} spots remaining!</p>
                </div>
              ) : (
                <p className="text-golf-sand text-xs">
                  {totalCapacity} total spots · {pct}% filled
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Countdown */}
      {showCountdown && (
        <div className="bg-gray-900 rounded-2xl p-5 mb-5 text-white">
          <p className="text-center text-sm font-semibold text-golf-sand mb-4">Registration Opens In</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Mins', value: timeLeft.minutes },
              { label: 'Secs', value: timeLeft.seconds },
            ].map((unit) => (
              <div key={unit.label} className="flex flex-col items-center bg-white/10 rounded-xl py-3">
                <span className="text-2xl font-display font-bold text-white leading-none">
                  {String(unit.value).padStart(2, '0')}
                </span>
                <span className="text-xs text-golf-sand mt-1">{unit.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      {isRegistrationOpen && !isCapacityReached ? (
        <button
          onClick={() => setIsOpen(true)}
          className="w-full bg-golf-orange text-white font-bold py-4 rounded-2xl text-base active:opacity-80 transition-opacity mb-5"
        >
          Register Now
        </button>
      ) : showCountdown ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-amber-700 text-sm leading-relaxed">
            Registration is not yet open. Please check back closer to the opening date.
          </p>
        </div>
      ) : isRegistrationEnabled && isCapacityReached ? null : (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-amber-700 text-sm leading-relaxed">
            Registration is currently disabled by the tournament organizers. Please check back later.
          </p>
        </div>
      )}

      {/* Registration Fee */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-3 text-center">
        <h3 className="font-display font-bold text-golf-gray text-base mb-2">Registration Fee</h3>
        <div className="text-3xl font-display font-bold text-golf-orange mb-1">
          {registrationFee.currency}{registrationFee.amount.toLocaleString()}
        </div>
        <p className="text-xs text-gray-500 mb-4">{registrationFee.description}</p>
        {feeInclusions.length > 0 && (
          <div className="flex flex-col gap-2 text-left">
            {feeInclusions.map((inclusion, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-golf-gray">
                <CheckIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
                <span>{inclusion}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="flex flex-col gap-3">
        {/* Registration Process */}
        {registrationSteps.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <h3 className="font-display font-bold text-golf-gray text-sm mb-3">Registration Process</h3>
            <div className="flex flex-col gap-3">
              {registrationSteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-golf-orange text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-golf-gray">{step.title}</p>
                    {step.description && <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>}
                    {step.date && <p className="text-xs text-golf-orange font-medium mt-0.5">{step.date}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Eligibility */}
        {eligibilityCriteria.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <h3 className="font-display font-bold text-golf-gray text-sm mb-3">Eligibility</h3>
            <ul className="flex flex-col gap-2">
              {eligibilityCriteria.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircleIcon className="w-4 h-4 text-golf-orange flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tournament Rules */}
        {rules.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <h3 className="font-display font-bold text-golf-gray text-sm mb-3">Tournament Rules</h3>
            <ul className="flex flex-col gap-2">
              {rules.map((rule, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircleIcon className="w-4 h-4 text-golf-orange flex-shrink-0 mt-0.5" />
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Important Notice */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
            <h3 className="font-display font-bold text-golf-gray text-sm">Important Notice</h3>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            {importantNotice || `Registration is on a first-come, first-served basis. The tournament is limited to ${totalCapacity} players${reservedSpots > 0 ? ` (with ${reservedSpots} spots reserved for special registrations)` : ''}. Early registration is recommended to secure your spot.`}
          </p>
          {!isRegistrationEnabled && (
            <p className="mt-3 text-red-600 font-medium text-xs">
              Registration is currently disabled by the tournament organizers. Please check back later for updates.
            </p>
          )}
          {isRegistrationEnabled && isCapacityReached && (
            <p className="mt-3 text-red-600 font-medium text-xs">
              Registration is currently closed as we have reached maximum capacity. Please check back later for any updates.
            </p>
          )}
        </div>
      </div>

      <RegistrationModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          fetchData();
        }}
      />
    </div>
  );
}
